import { Injectable } from '@angular/core';
import { ApiManagerService } from '@ildes/services/api-manager.service';
import { zip, from, of } from 'rxjs';
import { concatMap, tap, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as actions from '@ildes/views/technician-dashboard-maintenances/assigned-maintenances.actions';
import * as actionsLuminaries from '@ildes/views/list-luminaries/list-luminaries.actions';
import { SessionStore } from '@ildes/stores/session.store';
import { ImageCacheService } from './image-cache.service';
import questionsMaintenance from '@ildes/views/repair-maintenance/questions-maintenance';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root'
})
export class SyncDataService {
  constructor(
    public apiManagerService: ApiManagerService,
    private store: Store,
    private cacheService: ImageCacheService,
    private logger: LogService
  ) { }

  blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  getBase64FromCache(photo) {
    return new Promise(async (resolve) => {
      const response = await this.cacheService.get(photo);
      const buffer = await response.blob();
      const base64 = await this.blobToBase64(buffer)

      resolve(base64);
    })
  }

  syncMaintenance(maintenance, params = {updateUser: true, postpone: false}) {
    const options = {...{ updateUser: true, postpone: false}, ...params};

    let data;
    this.logger.info('syncMaintenance: init');

    let performInitialPhotos = of(null);
    let performFinalPhotos = of(null);

    this.logger.info('syncMaintenance: calculate photos');
    if (maintenance.photos?.initial) {
      this.logger.info('syncMaintenance: initial');
      performInitialPhotos = from(Promise.all(maintenance.photos.initial.map((photo) => {
        return new Promise((resolve) => {
          if (!photo.includes('/base64/')) {
            resolve(photo);

            return;
          }
          this.getBase64FromCache(photo).then((photo64) => {
            resolve(photo64);
          });
        });
      })));
      this.logger.info('syncMaintenance: end initial');
    }

    if (maintenance.photos?.final) {
      this.logger.info('syncMaintenance: final');
      performFinalPhotos = from(Promise.all(maintenance.photos.final.map((photo) => {
        return new Promise((resolve) => {
          if (!photo.includes('/base64/')) {
            resolve(photo);

            return;
          }
          this.getBase64FromCache(photo).then((photo64) => {
            resolve(photo64);
          });
        });
      })));
      this.logger.info('syncMaintenance: end initial');
    }
    this.logger.info('syncMaintenance: zip');
    return zip(
      performInitialPhotos,
      performFinalPhotos
    ).pipe((
      concatMap(([initialPhotos, finalPhotos]) => {
        const photos:any = {};
        this.logger.info('syncMaintenance: concatmap');
        if (initialPhotos) {
          photos.initial = initialPhotos;
          const sum = photos.initial.reduce((acum, str) => acum + str.length, 0);
          this.logger.info('photos initial '+sum);
        }
        if (finalPhotos) {
          photos.final = finalPhotos;
          const sum = photos.final.reduce((acum, str) => acum + str.length, 0);
          this.logger.info('photos final '+sum);
        }
        this.logger.info('syncMaintenance: prepare data');
        data = {
          ...maintenance.questionary && {
            questions: questionsMaintenance,
            answers: maintenance.questionary
          },
          ...maintenance.openDate && { openDate: maintenance.openDate },
          ...maintenance.luminary && { luminary: maintenance.luminary.number },
          ...Object.values(photos).length && { photos },
          state: maintenance.state,
          ...maintenance.localUsedMaterials && { usedMaterials: maintenance.usedMaterials },
          ...maintenance.extractedMaterials && { extractedMaterials: maintenance.extractedMaterials },
          ...maintenance.description && { description: maintenance.description },
          ...maintenance.activities && { activities: maintenance.activities },
          ...maintenance.newLuminaryNumber && { newLuminaryNumber: maintenance.newLuminaryNumber },
          ...maintenance.newTechnology && { newTechnology: maintenance.newTechnology },
          ...maintenance.newPower && { newPower: maintenance.newPower },
          ...maintenance.height && { height: maintenance.height },
          ...maintenance.ascendMethod && { ascendMethod: maintenance.ascendMethod },
          ...maintenance.repairmentDate && { repairmentDate: maintenance.repairmentDate },
          ...maintenance.luminaryOfTemporalUse && { luminaryOfTemporalUse: maintenance.luminaryOfTemporalUse },
          ...maintenance.allegedCauseOfFail && { allegedCauseOfFail: maintenance.allegedCauseOfFail },
        };
        this.logger.info('syncMaintenance: end prepare data');
        if (maintenance.pendingSync?.create) {
          const extraData = {
            project: typeof maintenance.project === typeof {} ? maintenance.project.id : maintenance.project,
            projectName: maintenance.project?.name,
            technician: maintenance.technician?.id,
            type: maintenance.type,
            painType: maintenance.painType,
            painDescription: maintenance.painDescription,
            luminaryNumber: maintenance.luminary?.number,
            luminary: typeof maintenance.luminary === typeof {} ? maintenance.luminary.id : maintenance.luminary,
            neighborhood: maintenance.neighborhood,
            address: maintenance.address,
            geolocation: maintenance.geolocation,
            estimatedResolutionDate: maintenance.estimatedResolutionDate,
            locationReferenceDescription: maintenance.locationReferenceDescription,
            luminaryOfTemporalUse: maintenance.luminaryOfTemporalUse,
            allegedCauseOfFail: maintenance.allegedCauseOfFail
          }

          data = {...data, ...extraData};
        }

        let service;
        if (maintenance.pendingSync?.create) {
            service = this.apiManagerService.createMaintenance(data);
        } else if (options.postpone || maintenance.postpone) {
            service = this.apiManagerService.postponeMaintenance(maintenance.id, data)
        } else {
            service = this.apiManagerService.updateMaintenance(maintenance.id, data)
        }

        this.logger.info('syncMaintenance: call service ');
        return service.pipe(
            tap( (data: any) => {
              this.logger.info('syncMaintenance: tap');
              this.store.dispatch(
                actions.sync({
                  maintenanceId: maintenance.id,
                  newMaintenanceId: maintenance.pendingSync?.create ? data?.maintenance?.id : null
                })
              )
            }),
            tap(() => {
              this.logger.info('syncMaintenance: tap2');
              if (!options.updateUser) {
                return;
              }
              this.logger.info('syncMaintenance: getuserdetail');
              this.apiManagerService.getUserDetail(maintenance.technician.id).subscribe((user) => {
                this.logger.info('syncMaintenance: getuserdetail done');
                SessionStore.updateUserInfo(user, true);
              })
            })
          );
      })
    ))
  }

  syncLuminary(luminary){
    const subscriber = luminary.id ?
      this.apiManagerService.updateLuminary(luminary.id, luminary):
      this.apiManagerService.createLuminary(luminary);


    return subscriber.pipe(
      tap( () => {
        this.store.dispatch(
          actionsLuminaries.remove({
            _id: luminary._id
          })
        )
      }),
    );
  }
}
